import { useState, useRef, useEffect } from "react";

const useDragAndDrop = (initialItems, direction = "vertical", setReadyToUpdate) => {
  const [items, setItems] = useState(initialItems);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const placeholderRef = useRef(null);
  const hiddenItemRef = useRef(null);

  const containerRef = useRef(null);
  const ghostRef = useRef(null);
  const originalItemRef = useRef(null);
  const itemRefs = useRef([]);

  // Initialize item refs
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, initialItems.length);
  }, [initialItems.length]);

  useEffect(() => {
    setReadyToUpdate(false);
    setItems(initialItems);
  }, [initialItems]);

  const handleMouseDown = (index, e) => {
    // Remove drag drop if item length is 1
    if (items?.length <= 1) return;

    // Define interactive elements and no-drag classes
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "A"];
    const noDragClasses = [
      "no-drag",
      "dropdown",
      "dropdown-item",
      "form-group",
      "custom-control-input",
      "custom-checkbox",
      "dropdown-menu",
      "delete-row",
    ];

    // Check if target or any parent is interactive
    let currentElement = e.target;
    let shouldPreventDrag = false;

    while (currentElement && currentElement !== e.currentTarget) {
      const isInteractiveTag = interactiveTags.includes(currentElement.tagName);
      const hasNoDragClass = noDragClasses.some(className => currentElement.classList.contains(className));
      const isContentEditable = currentElement.contentEditable === "true";

      if (isInteractiveTag || hasNoDragClass || isContentEditable) {
        shouldPreventDrag = true;
        break;
      }
      currentElement = currentElement.parentElement;
    }
    if (shouldPreventDrag) {
      return; // Don't start drag if interactive element was clicked
    }

    e.preventDefault();
    setDraggingIndex(index);

    const originalItem = itemRefs.current[index];
    const ghost = originalItem.cloneNode(true);
    const rect = originalItem.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Copy all computed styles from original item
    const computedStyles = window.getComputedStyle(originalItem);
    Array.from(computedStyles).forEach(prop => {
      ghost.style[prop] = computedStyles.getPropertyValue(prop);
    });

    const initialLeft = rect.left - containerRect.left + containerRef.current.scrollLeft;

    const initialTop = rect.top - containerRect.top + containerRef.current.scrollTop;

    // Apply ghost styles
    ghost.className = `${originalItem.className}`;
    ghost.style.position = "absolute"; // set ghost image position
    ghost.style.left = `${initialLeft}px`;
    ghost.style.top = `${initialTop}px`;
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.margin = "0";
    ghost.style.pointerEvents = "none";
    ghost.style.zIndex = "1000";
    ghost.style.opacity = "0.9";

    // Append ghost INSIDE container
    containerRef.current.appendChild(ghost);
    ghostRef.current = ghost;

    // originalItemRef.current = rect;
    originalItemRef.current = {
      initialLeft,
      initialTop,
      startClientX: e.clientX,
      startClientY: e.clientY,
    };

    // Hide original item
    originalItem.style.visibility = "hidden";
  };

  const handleMouseMove = e => {
    if (draggingIndex === null || !ghostRef.current) return;

    setIsDragging(true);

    // Calculate position relative to container
    const { initialLeft, initialTop, startClientX, startClientY } = originalItemRef.current;

    const deltaX = e.clientX - startClientX;
    const deltaY = e.clientY - startClientY;

    let newX = initialLeft + deltaX;
    let newY = initialTop + deltaY;

    const container = containerRef.current;

    newX = Math.max(0, Math.min(newX, container.scrollWidth - ghostRef.current.offsetWidth));

    newY = Math.max(0, Math.min(newY, container.scrollHeight - ghostRef.current.offsetHeight));

    ghostRef.current.style.left = `${newX}px`;
    ghostRef.current.style.top = `${newY}px`;

    const newHoverIndex = itemRefs.current.findIndex((item, idx) => {
      if (idx === draggingIndex || !item) return false;
      const rect = item.getBoundingClientRect();
      return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    });

    if (newHoverIndex !== -1 && newHoverIndex !== draggingIndex) {
      // Show the item we're about to swap with (item2)
      itemRefs.current[draggingIndex].style.visibility = "visible";

      // Remove existing placeholder if any
      if (placeholderRef.current && placeholderRef.current.parentNode) {
        placeholderRef.current.parentNode.removeChild(placeholderRef.current);
        placeholderRef.current = null;
      }

      if (hiddenItemRef.current) {
        hiddenItemRef.current.style.display = ""; // Restore visibility
        hiddenItemRef.current = null;
      }

      const original = itemRefs.current[newHoverIndex];
      if (original) {
        const rect = original.getBoundingClientRect();

        const placeholder = document.createElement("div");
        placeholder.style.width = `${rect.width}px`;
        placeholder.style.height = `${rect.height}px`;
        placeholder.style.border = "2px dashed #888";
        placeholder.style.background = "transparent";
        placeholder.style.boxSizing = "border-box";
        placeholder.style.margin = window.getComputedStyle(original).margin;

        original.parentNode.insertBefore(placeholder, original);
        original.style.display = "none";
        placeholderRef.current = placeholder;
        hiddenItemRef.current = original;
      }
      setReadyToUpdate(true);

      // Perform the swap
      setItems(prevItems => {
        const newItems = [...prevItems];
        [newItems[draggingIndex], newItems[newHoverIndex]] = [newItems[newHoverIndex], newItems[draggingIndex]];
        return newItems;
      });

      // Update dragging index to track our item's new position
      setDraggingIndex(newHoverIndex);
    }
  };

  const handleMouseUp = () => {
    if (draggingIndex === null) return;

    // Remove ghost from container
    if (ghostRef.current && containerRef.current.contains(ghostRef.current)) {
      containerRef.current.removeChild(ghostRef.current);
    }
    ghostRef.current = null;

    // Reset all borders
    itemRefs.current.forEach(item => {
      if (item) item.style.border = "";
    });

    // Show original item
    if (itemRefs.current[draggingIndex]) {
      itemRefs.current[draggingIndex].style.visibility = "visible";
    }
    // Remove placeholder and restore original display
    if (placeholderRef.current && placeholderRef.current.parentNode) {
      placeholderRef.current.parentNode.removeChild(placeholderRef.current);
      placeholderRef.current = null;
    }
    if (itemRefs.current[draggingIndex]) {
      itemRefs.current[draggingIndex].style.display = "";
    }

    // Reorder items if needed
    if (hoverIndex !== null && hoverIndex !== draggingIndex) {
      const newItems = [...items];
      const [removed] = newItems.splice(draggingIndex, 1);
      newItems.splice(hoverIndex, 0, removed);
      setItems(newItems);
      setReadyToUpdate(true);
    }

    // Reset state
    setDraggingIndex(null);
    setHoverIndex(null);
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  return {
    items,
    containerProps: {
      ref: containerRef,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      style: { position: "relative" },
    },
    getItemProps: index => ({
      ref: el => (itemRefs.current[index] = el),
      onMouseDown: e => handleMouseDown(index, e),
      style: {
        position: "relative",
        ...(hoverIndex === index && {
          [direction === "horizontal" ? "marginLeft" : "marginTop"]:
            draggingIndex < index
              ? `-${itemRefs.current[draggingIndex]?.offsetWidth || 0}px`
              : `${itemRefs.current[draggingIndex]?.offsetWidth || 0}px`,
          transition: "margin 0.2s ease",
        }),
      },
    }),
    isDragging,
  };
};

export default useDragAndDrop;
